import MarkdownEditor from "@uiw/react-markdown-editor";
import { Timestamp } from "firebase/firestore";
import { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import * as icon from "react-bootstrap-icons";
import DataTable, { TableColumn } from "react-data-table-component";
import useCubit from "../../../blocs/hooks";
import { allRights, getUserRights, userHasRight } from "../../../core/models/User";
import { kNavBarHeight } from "../../../core/utils/constants";
import { LoadingData } from "../../LoadingData";
import NoData from "../../NoData";
import { DataTableBloc, IDataTableBloc } from "./DataTableBloc";
import { AddEditModalProps, AdminDataTableBodyProps, AdminDataTableProps, Column, DeleteModalProps, RenderEditCellsProps, ShowDetailsModalProps, createEmptyObjectFromColumns, rowData, rowDataObject } from "./DataTableInterfaces";
import { DataTableState } from "./DataTableState";


export default function AdminDataTable(props: AdminDataTableProps) {
    const cubit: IDataTableBloc = new DataTableBloc(props.collection, props.columns, props.repository);
    return <AdminDataTableBody
        cubit={cubit}
        collection={props.collection}
        columns={props.columns}
    />
}



function AdminDataTableBody(props: AdminDataTableBodyProps) {
    const state: DataTableState = useCubit<DataTableState>(props.cubit);
    // TODO: turn it back when you implement user rights for the data table
    // const authState: AuthState = useCubit<AuthState>(AuthCubit.getInstance());
    const [selectedData, setSelectedData] = useState<Object | undefined>();
    const [selectedDataID, setSelectedDataID] = useState<string | undefined>();
    const [showAddEditModal, setShowAddEditModal] = useState<boolean>(false);
    const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);


    function cellRenderer(row: Object, col: Column): JSX.Element {
        if ((row as any)[col.field] !== undefined) {
            if (col.type === 'image') {
                return <img alt="#" style={{ height: 66 }} src={rowData(row, col)} />
            }
            else if (col.type === 'gallery') {
                return <tr style={{ margin: 8 }} key={col.field}>
                    <th style={{ width: "100%" }}>
                        {
                            rowData(row, col) !== undefined && (rowData(row, col) as string[]).map((imageUrl: string) => {
                                return <a href={imageUrl} target="_blank" rel="noreferrer">
                                    <img alt="#" style={{ width: 60 }} src={imageUrl} />
                                </a>
                            })
                        }
                    </th>
                </tr>
            } else if (col.type === 'file') {
                return <a href={rowData(row, col)} target="_blank" rel="noreferrer">file: {(JSON.parse(JSON.stringify(row))).id}</a>
            } else if (['date', 'datetime'].includes(col.type)) {
                return <p>{new Timestamp((rowData(row, col) as Timestamp).seconds, (rowData(row, col) as Timestamp).nanoseconds).toDate().toLocaleDateString()}</p>
            } else if (col.type === 'object') {
                return <p>{JSON.stringify(rowData(row, col))}</p>
            } else if (col.type === 'hashtags') {
                return <p>{((row as any)[col.field] as Array<string>).toString()}</p>
            } else {
                return <p>{rowData(row, col)}</p>
            }
        }
        return <></>;
    }
    const columns: TableColumn<Object>[] = props.columns.filter(col => !(col.show === false)).map<TableColumn<Object>>(col => {
        return {
            name: col.name ?? col.field,
            selector: row => rowData(row, col),
            cell: row => cellRenderer(row, col)
        }
    })
    columns.push({
        name: 'Actions',
        cell: row => [
            <Button style={{ borderRadius: 90, margin: 4 }} variant="outline-success" onClick={() => {
                setSelectedDataID(rowDataObject<Object>(row).id);
                setSelectedData(rowDataObject<Object>(row));
                setShowAddEditModal(true);
            }}><icon.Pencil /> </Button>,
            <Button style={{ borderRadius: 90, margin: 4 }} variant="outline-warning" onClick={() => {
                setSelectedDataID(rowDataObject<Object>(row).id);
                setSelectedData(rowDataObject<Object>(row));
                setShowDetailsModal(true);
            }}><icon.Eye /></Button>,
            <Button style={{ borderRadius: 90, margin: 4 }} variant="outline-danger" onClick={() => {
                setSelectedDataID(rowDataObject<Object>(row).id);
                setShowDeleteModal(true)
            }}><icon.Trash /></Button>
        ]
    })
    if (state.state !== 'LOADING') {
        return <div style={{
            marginTop: kNavBarHeight
        }}>
            <AddEditModal<Object>
                show={showAddEditModal}
                columns={props.columns}
                collection={props.collection}
                data={selectedData}
                onClose={async () => {
                    setShowAddEditModal(false);
                    setSelectedData(undefined);
                    setSelectedDataID(undefined);
                }}
                onSave={(data) => {
                    props.cubit.add(data);
                }}
                onUpdate={(data) => {
                    props.cubit.update(data, selectedDataID!);
                }}
                onUploadFile={props.cubit.uploadFile}
            />
            <DeleteModal
                show={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setSelectedDataID(undefined)
                    setSelectedData(undefined)
                }}
                onDelete={() => props.cubit.delete(selectedDataID!)}
            />
            <ShowDetailsModal
                show={showDetailsModal}
                columns={props.columns}
                collection={props.collection}
                data={selectedData}
                onClose={() => {
                    setShowDetailsModal(false)
                    setSelectedDataID(undefined)
                    setSelectedData(undefined)
                }}
            />
            <DataTable<Object>
                columns={columns}
                data={state.data!}
                noDataComponent={<NoData onTap={() => { setShowAddEditModal(true) }} />}
                actions={[
                    <Button variant="outline-dark" style={{ borderRadius: 90, margin: 4 }} onClick={() => setShowAddEditModal(true)} >✍ Add</Button>,
                ]}
            />
        </div>
    } else {
        return <LoadingData />
    }
}

function DeleteModal(props: DeleteModalProps) {
    return <Modal show={props.show} onHide={props.onClose}>
        <Modal.Header closeButton>
            <Modal.Title>Are you sure?</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete? this action in unreversible</Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={props.onClose}>
                Close
            </Button>
            <Button variant="danger" onClick={() => {
                props.onDelete()
                props.onClose()
            }}>
                Delete
            </Button>
        </Modal.Footer>
    </Modal>
}

function RenderEditCells<Object>(props: RenderEditCellsProps<Object>): JSX.Element {
    const [_, setLocalData] = useState<Object>(props.data)
    if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        console.log("Current edit cell data", _);
    }
    return <>{props.columns.filter(col => col.edit !== false).map(col => {
        if ((col.type === 'image' || col.type === 'file')) {
            return <tr style={{ margin: 8 }} key={col.field}>
                <th>{col.name ?? col.field}</th>
                <th style={{ width: "100%" }}>
                    {
                        col.type === 'image'
                        && (((props.data as any)[col.field] as string)?.length ?? 0) > 0
                        && <><a href={rowDataObject<Object>(props.data!)[col.field]} target="_blank" rel="noreferrer">
                            <img alt="#" style={{ maxWidth: 300, minWidth: 150 }} src={rowDataObject<Object>(props.data!)[col.field]} />
                        </a><br></br></>
                    }
                    <input
                        type="file"
                        onChange={async ev => {
                            if (ev.target.files !== null) {
                                (props.data as any)[col.field] = await props.onUploadFile!(ev.target.files[0].name, ev.target.files[0]);
                                setLocalData(props.data);
                                props.onFieldValueChanged();
                            }
                        }}
                    ></input>
                </th>
            </tr>
        }
        else if (col.type === 'gallery') {
            return <tr style={{ margin: 8 }} key={col.field}>
                <th>{col.name ?? col.field}</th>
                <th style={{ width: "100%" }}>
                    {
                        (props.data as any)[col.field] !== undefined && (rowDataObject<Object>(props.data!)[col.field] as string[]).map((imageUrl: string) => {
                            return <a href={imageUrl} target="_blank" rel="noreferrer">
                                <img alt="#" style={{ width: 60 }} src={imageUrl} />
                            </a>
                        })
                    }
                    <input
                        type="file"
                        onChange={async ev => {
                            if (ev.target.files !== null) {
                                if ((props.data as any)[col.field] === undefined) {
                                    (props.data as any)[col.field] = [];
                                }
                                ((props.data as any)[col.field] as string[]).push(await props.onUploadFile!(ev.target.files[0].name, ev.target.files[0]));
                                setLocalData(props.data);
                                props.onFieldValueChanged();
                            }
                        }}
                    ></input>
                </th>
            </tr>
        }
        else if (['datestring', 'date', 'datetime'].includes(col.type)) {
            const dateObject: Date = Object.hasOwn(Object(props.data!), col.field) && (props.data as any)[col.field] !== undefined
                ? new Timestamp((rowDataObject<Object>(props.data!)[col.field] as Timestamp).seconds, (rowDataObject<Object>(props.data!)[col.field] as Timestamp).nanoseconds).toDate()
                : new Date();
            return <tr style={{ margin: 8 }} key={col.field}>
                <th>{col.name ?? col.field}</th>
                <th style={{ width: "100%" }}>
                    <input
                        type="date"
                        onChange={ev => {
                            (props.data as any)[col.field] = ev.target.value;
                            setLocalData(props.data);
                            props.onFieldValueChanged();
                        }}
                        defaultValue={dateObject.toISOString().split('Object')[0]}
                    ></input>
                </th>
            </tr>
        }
        else if (col.type === 'feature') {
            let features: JSX.Element[] = []
            for (const right of Object.keys(allRights).map(e => parseInt(e))) {
                features.push(<div>
                    <input
                        key={`Feature#${right}`}
                        type="checkbox"
                        defaultChecked={userHasRight(right, rowData(props.data, col))}
                        onChange={ev => {
                            if (ev.target.checked) {
                                (props.data as any)[col.field] = (props.data as any)[col.field] | right;
                            } else {
                                (props.data as any)[col.field] = (props.data as any)[col.field] ^ right;
                            }
                            setLocalData(props.data);
                            props.onFieldValueChanged();
                        }}
                    />{allRights[right]}</div>)
            }
            return <tr>
                <th>{col.name}</th>
                <th style={{ width: "100%" }}>
                    {features}
                </th>
            </tr>;

        }
        else if (col.type === 'object') {
            let newProps: RenderEditCellsProps<Object> = { ...props }
            newProps.columns = col.has!
            newProps.data = (props.data ?? {} as any)[col.field]
            return RenderEditCells(newProps);
        }
        else if (col.type === 'markdown') {
            return <>
                <tr>
                    <th>{col.name}</th>
                </tr>
                <tr>
                    <th style={{ width: "100%" }}>
                        <MarkdownEditor
                            value={(props.data as any)[col.field]}
                            onChange={(mdString) => {
                                (props.data as any)[col.field] = mdString;
                                setLocalData(props.data!);
                                props.onFieldValueChanged();
                            }}
                        />
                    </th>
                </tr>
            </>
        }
        return <tr style={{ margin: 8 }} key={col.field}>
            <th>{col.name ?? col.field}</th>
            <th style={{ width: "100%" }}>
                <input
                    type="text"
                    onChange={ev => {
                        (props.data as any)[col.field] = ev.target.value;
                        setLocalData(props.data!);
                        props.onFieldValueChanged();
                    }}
                    defaultValue={props.data && rowDataObject<Object>(props.data!)[col.field]}
                ></input>
            </th>
        </tr>
    })}</>;
}

function AddEditModal<Object>(props: AddEditModalProps<Object>) {
    let data = (props.data ?? createEmptyObjectFromColumns(props.columns)) as Object;
    const [_, setRefresh] = useState<boolean>(true);
    if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        console.log("Current AddEditModal data", _);
    }
    return <Modal show={props.show} onHide={props.onClose} size="lg">
        <Modal.Header closeButton>
            <Modal.Title>{props.data === undefined ? "Add" : "Edit"} {props.collection}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <table>
                <tbody style={{ padding: 4 }}>
                    <RenderEditCells
                        columns={props.columns}
                        data={data}
                        onFieldValueChanged={() => { setRefresh(true) }}
                        onUploadFile={props.onUploadFile}
                    />
                </tbody>
            </table>
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={props.onClose}>
                Close
            </Button>
            <Button variant="primary" onClick={() => {
                if (data !== undefined) {
                    if (props.data === undefined) {
                        props.onSave(data!);
                    } else {
                        props.onUpdate!(data!);
                    }
                }
                props.onClose();
            }}>
                {props.data === undefined ? "Save Changes" : "Update Changes"}
            </Button>
        </Modal.Footer>
    </Modal>
}

function ShowDetailsModal<Object>(props: ShowDetailsModalProps<Object>) {
    function renderCell(props: ShowDetailsModalProps<Object>): JSX.Element {
        if (props.columns === undefined) {
            throw Error("props.columns is undefined, did you forget to declare them in the DataTable schema??");
        } else if (props.data === undefined) {
            return <></>;
        }
        return <>{props.columns.map(col => {
            if (col.type === 'image') {
                return <tr style={{ margin: 8 }} key={col.field}>
                    <th>{col.name ?? col.field}</th>
                    <th style={{ width: "100%" }}>
                        {
                            Object.hasOwn(Object(props.data), col.field)
                            && col.type === 'image'
                            && <a href={rowDataObject<Object>(props.data!)[col.field]} target="_blank" rel="noreferrer">
                                <img alt="#" style={{ maxWidth: 300, minWidth: 150 }} src={rowDataObject<Object>(props.data!)[col.field]} />
                            </a>
                        }
                    </th>
                </tr>
            }
            else if (col.type === 'gallery') {
                return <tr style={{ margin: 8 }} key={col.field}>
                    <th>{col.name ?? col.field}</th>
                    <th style={{ width: "100%" }}>
                        {
                            (props.data as any)[col.field] !== undefined && (rowDataObject<Object>(props.data!)[col.field] as string[]).map((imageUrl: string) => {
                                return <a href={imageUrl} target="_blank" rel="noreferrer">
                                    <img alt="#" style={{ width: 60 }} src={imageUrl} />
                                </a>
                            })
                        }
                    </th>
                </tr>
            }
            else if (col.type === 'file') {
                if (rowDataObject<Object>(props.data!)[col.field] === undefined) {
                    return <></>
                } else {
                    return <tr style={{ margin: 8 }} key={col.field}>
                        <th>{col.name ?? col.field}</th>
                        <th style={{ width: "100%" }}>
                            <a
                                href={rowDataObject<Object>(props.data!)[col.field]}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Show file
                            </a>
                        </th>
                    </tr>
                }
            }
            else if (['datestring', 'date', 'datetime'].includes(col.type)) {
                const dateObject: Date = Object.hasOwn(Object(props.data!), col.field)
                    ? new Timestamp((rowDataObject<Object>(props.data!)[col.field] as Timestamp).seconds, (rowDataObject<Object>(props.data!)[col.field] as Timestamp).nanoseconds).toDate()
                    : new Date();
                return <tr style={{ margin: 8 }} key={col.field}>
                    <th>{col.name ?? col.field}</th>
                    <th style={{ width: "100%" }}>
                        {dateObject.toLocaleDateString()}
                    </th>
                </tr>
            }
            else if (col.type === 'feature') {
                return <tr>
                    <th>{col.name}</th>
                    <th style={{ width: "100%" }}>
                        {getUserRights(rowData(props.data, col))}
                    </th>
                </tr>
            }
            else if (col.type === 'object' && props.data !== undefined) {
                let newProps = { ...props }
                newProps.columns = col.has!
                newProps.data = rowData(props.data, col)
                return renderCell(newProps);
            }
            else if (col.type === 'markdown' && props.data !== undefined) {
                return <>
                    <tr>
                        <th>{col.name}</th>
                    </tr>
                    <tr>
                        <th style={{ width: "100%" }}>
                            <MarkdownEditor.Markdown
                                source={(props.data as any)[col.field]}
                            />
                        </th>
                    </tr>
                </>
            }
            return <tr style={{ margin: 8 }} key={col.field}>
                <th>{col.name ?? col.field}</th>
                <th style={{ width: "100%" }}>
                    {Object.hasOwn(Object(props.data), col.field) && rowDataObject<Object>(props.data!)[col.field]}
                </th>
            </tr>
        })}</>
    }

    const fields = renderCell(props);

    return <Modal show={props.show} onHide={props.onClose} size="lg">
        <Modal.Header closeButton>
            <Modal.Title>Show details of: {props.collection}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <table>
                <tbody style={{ padding: 4 }}>
                    {fields}
                </tbody>
            </table>
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={props.onClose}>
                Close
            </Button>
        </Modal.Footer>
    </Modal>
}
